import { Injectable } from '@angular/core';
import { OfferStepsEnum } from '@app/enums/offer-steps.enum';
import { FillingScore250 } from '@app/services/filling-score/filling-score-250';
import { FillingScore500 } from '@app/services/filling-score/filling-score-500';
import { FillingScore750 } from '@app/services/filling-score/filling-score-750';
import { FillingScore1000 } from '@app/services/filling-score/filling-score-1000';
import { FillingScore2000 } from '@app/services/filling-score/filling-score-2000';
import { FillingScore2200 } from '@app/services/filling-score/filling-score-2200';
import { FillingScore2450 } from '@app/services/filling-score/filling-score-2450';
import { FillingScore2700 } from '@app/services/filling-score/filling-score-2700';
import { FillingScore2800 } from '@app/services/filling-score/filling-score-2800';
import { FillingScore2850 } from '@app/services/filling-score/filling-score-2850';
import { FillingScore2900 } from '@app/services/filling-score/filling-score-2900';
import { FillingScore3000 } from '@app/services/filling-score/filling-score-3000';
import { FillingScore4000 } from '@app/services/filling-score/filling-score-4000';
import { FillingScore3500 } from '@app/services/filling-score/filling-score-3500';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

export interface FillingScoreParamInterface {
    mortgage: MortgageInterface;
    scoreByStepUntil?: number;
}

export interface FillingScoreResultInterface {
    scoreChanged: boolean;
    fillingScore: number;
    fillingScoreUpdatedAt?: Date;
    scoreByStepUntil?: number;
}

interface FillingScoreStepDefinition {
    targetScore: number;
    previousScore: number;
    checker: { check: (mortgage: MortgageInterface) => boolean };
    description: string;
}

@Injectable({
    providedIn: 'root',
})
export class FillingScoreService {
    private static readonly SCORE_STEPS: FillingScoreStepDefinition[] = [
        {
            previousScore: 0,
            targetScore: OfferStepsEnum.Calculator,
            checker: FillingScore250,
            description: '250 - Calculator',
        },
        {
            previousScore: OfferStepsEnum.Calculator,
            targetScore: OfferStepsEnum.Applicants,
            checker: FillingScore500,
            description: '500 - Applicants',
        },
        {
            previousScore: OfferStepsEnum.Applicants,
            targetScore: OfferStepsEnum.Additional,
            checker: FillingScore750,
            description: '750 - Additional',
        },
        {
            previousScore: OfferStepsEnum.Additional,
            targetScore: OfferStepsEnum.Offers,
            checker: FillingScore1000,
            description: '1000 - Offers',
        },
        {
            previousScore: OfferStepsEnum.Offers,
            targetScore: OfferStepsEnum.DocumentsHome,
            checker: FillingScore2000,
            description: '2000 - DocumentsHome',
        },
        {
            previousScore: OfferStepsEnum.DocumentsHome,
            targetScore: OfferStepsEnum.DocumentsMoney,
            checker: FillingScore2200,
            description: '2200 - DocumentsMoney',
        },
        {
            previousScore: OfferStepsEnum.DocumentsMoney,
            targetScore: OfferStepsEnum.DocumentsAdditional,
            checker: FillingScore2450,
            description: '2450 - DocumentsAdditional',
        },
        {
            previousScore: OfferStepsEnum.DocumentsAdditional,
            targetScore: OfferStepsEnum.DocumentsIdentification,
            checker: FillingScore2700,
            description: '2700 - DocumentsIdentification',
        },
        {
            previousScore: OfferStepsEnum.DocumentsIdentification,
            targetScore: OfferStepsEnum.DocumentsApplication,
            checker: FillingScore2800,
            description: '2800 - DocumentsApplication',
        },
        {
            previousScore: OfferStepsEnum.DocumentsApplication,
            targetScore: OfferStepsEnum.DocumentsApproval,
            checker: FillingScore2850,
            description: '2850 - DocumentsApproval',
        },
        {
            previousScore: OfferStepsEnum.DocumentsApproval,
            targetScore: OfferStepsEnum.DocumentsMortgage,
            checker: FillingScore2900,
            description: '2900 - DocumentsMortgage',
        },
        {
            previousScore: OfferStepsEnum.DocumentsMortgage,
            targetScore: OfferStepsEnum.ForDrawing,
            checker: FillingScore3000,
            description: '3000 - ForDrawing',
        },
        {
            previousScore: OfferStepsEnum.ForDrawing,
            targetScore: OfferStepsEnum.AfterDrawing,
            checker: FillingScore3500,
            description: '3500 - AfterDrawing',
        },
        {
            previousScore: OfferStepsEnum.AfterDrawing,
            targetScore: OfferStepsEnum.Caretaker,
            checker: FillingScore4000,
            description: '4000 - Caretaker',
        },
    ];

    public static calculate(params: FillingScoreParamInterface): FillingScoreResultInterface {
        let result = {
            scoreChanged: false,
            fillingScore: 0,
            fillingScoreUpdatedAt: null,
            scoreByStepUntil: params.scoreByStepUntil,
        };

        const { mortgage } = params;

        const isScoreByStep = FillingScoreService.isScoreByStepActive(mortgage);

        if (isScoreByStep && mortgage.isPlatformChanged) {
            return result;
        }

        const calculatedScore = FillingScoreService.calculateNewScore(mortgage, isScoreByStep);
        const shouldUpdateScore = FillingScoreService.shouldUpdateScore(mortgage, calculatedScore);

        if (shouldUpdateScore) {
            result.scoreChanged = true;
            result.fillingScore = calculatedScore;
            result.fillingScoreUpdatedAt = new Date();
        }

        if (mortgage.scoreByStepUntil === calculatedScore) {
            result.scoreByStepUntil = null;
        }

        return result;
    }

    private static isScoreByStepActive(mortgage: MortgageInterface): boolean {
        return !!(
            mortgage.scoreByStepUntil &&
            mortgage.scoreByStepUntil < OfferStepsEnum.DocumentsApplication &&
            mortgage.scoreByStepUntil > mortgage.fillingScore
        );
    }

    private static calculateNewScore(mortgage: MortgageInterface, isScoreByStep: boolean): number {
        let currentScore = FillingScoreService.getInitialScore(mortgage, isScoreByStep);
        let scoreByStepDone = false;

        for (const step of FillingScoreService.SCORE_STEPS) {
            if (FillingScoreService.canProcessStep(isScoreByStep, scoreByStepDone, currentScore, step)) {
                if (step.checker.check(mortgage)) {
                    scoreByStepDone = true;
                    currentScore = step.targetScore;
                }
            }
        }

        return currentScore;
    }

    private static getInitialScore(mortgage: MortgageInterface, isScoreByStep: boolean): number {
        if (isScoreByStep) {
            return mortgage.fillingScore;
        }
        return mortgage.fillingScore >= OfferStepsEnum.ForDrawing ? mortgage.fillingScore : 0;
    }

    private static canProcessStep(
        isScoreByStep: boolean,
        scoreByStepDone: boolean,
        currentScore: number,
        step: FillingScoreStepDefinition
    ): boolean {
        return (!isScoreByStep || !scoreByStepDone) && currentScore === step.previousScore;
    }

    private static shouldUpdateScore(mortgage: MortgageInterface, newScore: number): boolean {
        const currentFillingScore = mortgage.fillingScore;

        if (currentFillingScore === undefined) {
            return true;
        }

        // Pokud je fillingScore mensi nez 3000 a fillingScore je ruzne od score, nastavime nove score a
        // je jedno jestli bude vyssi nebo nizsi nez puvodni
        if (currentFillingScore < OfferStepsEnum.ForDrawing && currentFillingScore !== newScore) {
            return true;
        }

        // Nebo pokud je fillingScore alespon 3000, nastavime nove score jen v pripade ze je vyssi nez puvodni
        if (currentFillingScore >= OfferStepsEnum.ForDrawing && currentFillingScore < newScore) {
            return true;
        }

        return false;
    }
}
